/**
 * Location capture for punch auditing.
 *
 * The first version of this raced the browser's own permission dialog against a
 * 4-second timeout. A person reading "Allow hourtrax.com to use your location?"
 * takes longer than that to decide, so the timeout fired first, the punch went
 * up with no coordinates, and the employee was told to "turn on location in
 * your browser settings" — while the prompt was still on screen. It looked like
 * the app never asked. It had asked; it just gave up before they could answer.
 *
 * So: find out what state the permission is actually in, and only run a clock
 * against the cases where nothing is being asked of the user.
 */

const FAST_TIMEOUT_MS = 6000;    // already granted — a fix should be quick
const PROMPT_TIMEOUT_MS = 45000; // a human is reading a dialog; do not rush them

/**
 * 'granted' | 'prompt' | 'denied' | 'unsupported' | 'unknown'
 *
 * The Permissions API is the only way to distinguish "will show a prompt" from
 * "was already refused", and the difference matters: a denied permission cannot
 * be re-requested from script, so asking again is pointless and the user needs
 * instructions instead. Safari only supported geolocation here from 16.4, hence
 * 'unknown' rather than assuming either way.
 */
export async function getGeoPermissionState() {
  if (!('geolocation' in navigator)) return 'unsupported';
  if (!navigator.permissions?.query) return 'unknown';
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state; // granted | prompt | denied
  } catch {
    return 'unknown';
  }
}

function readPosition(timeoutMs) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };
    const timer = setTimeout(() => finish({ location: null, reason: 'timeout' }), timeoutMs);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(timer);
        finish({
          location: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          },
          reason: null,
        });
      },
      (err) => {
        clearTimeout(timer);
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        finish({ location: null, reason: err && err.code === 1 ? 'denied' : 'unavailable' });
      },
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60000 }
    );
  });
}

/**
 * Ask for location, prompting the user when the browser will still allow it.
 *
 * Returns { location, reason }. `location` is null when no fix was obtained;
 * `reason` is 'denied' | 'timeout' | 'unavailable' | 'unsupported' and tells the
 * caller whether to show recovery instructions (denied — the browser will not
 * ask again) or simply to try once more.
 *
 * `onPrompting` fires when a permission dialog is expected to be on screen, so
 * the UI can say what the user is looking at instead of showing a bare spinner.
 */
export async function requestLocation({ onPrompting } = {}) {
  const state = await getGeoPermissionState();

  if (state === 'unsupported') return { location: null, reason: 'unsupported' };
  // Denied is terminal for this page load: getCurrentPosition fails instantly
  // without asking, so do not pretend we are still trying.
  if (state === 'denied') return { location: null, reason: 'denied' };

  if (state === 'prompt') {
    if (onPrompting) onPrompting();
    return readPosition(PROMPT_TIMEOUT_MS);
  }

  // 'granted', or 'unknown' where we cannot tell. 'unknown' may still surface a
  // prompt (older Safari), so give that the patient timeout too.
  return readPosition(state === 'granted' ? FAST_TIMEOUT_MS : PROMPT_TIMEOUT_MS);
}

/**
 * Best-effort one-shot read that never rejects and resolves the bare location
 * or null. For callers that only want coordinates for auditing and have no UI
 * in which to explain a permission decision.
 */
export async function getLocationOnce(timeoutMs = FAST_TIMEOUT_MS) {
  if (!('geolocation' in navigator)) return null;
  const { location } = await readPosition(timeoutMs);
  return location;
}

/**
 * Is this running as an installed app rather than a browser tab?
 *
 * Matters for recovery instructions: an installed HourTrax launches from the
 * home screen with display:standalone, so there is no address bar and no lock
 * icon to tap. Its permissions live under the operating system's app settings
 * instead, and telling someone to tap a control that is not on screen is worse
 * than saying nothing.
 */
export function isInstalledApp() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * How to re-enable location once it has been refused, which cannot be done from
 * script. Wording is per-platform AND per-context, because the setting lives
 * somewhere different on each, and "check your browser settings" is exactly the
 * instruction people cannot act on — especially in an installed app, where
 * there is no browser chrome at all.
 */
export function locationRecoverySteps() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
  const isAndroid = /Android/.test(ua);
  const installed = isInstalledApp();

  if (installed && isAndroid) {
    return [
      'Open your phone\'s Settings app (not HourTrax).',
      'Go to Apps, then find HourTrax — the same name as your home screen icon.',
      'Tap Permissions, then Location, and choose "Allow only while using the app".',
      'If Location is greyed out: Settings › Apps › Chrome › Permissions › Location must be allowed too, and Settings › Location must be switched on.',
      'Come back here and try again.',
    ];
  }
  if (installed && isIOS) {
    return [
      'Open your phone\'s Settings app (not HourTrax).',
      'Go to Privacy & Security, then Location Services.',
      'Make sure Location Services is on, then find HourTrax in the list and choose "While Using the App".',
      'Come back here and try again.',
    ];
  }
  if (isIOS) {
    return [
      'Tap the "aA" button on the left of the address bar.',
      'Choose "Website Settings".',
      'Set Location to "Allow", then reload this page.',
      'Still blocked? Open Settings › Privacy & Security › Location Services and make sure it is on for your browser.',
    ];
  }
  if (isAndroid) {
    return [
      'Tap the icon to the left of the address bar (a lock, or sliders).',
      'Tap "Permissions", then set Location to "Allow".',
      'Reload this page and try again.',
      'Nothing there? Settings › Apps › Chrome › Permissions › Location must be allowed, and Settings › Location switched on.',
    ];
  }
  return [
    'Click the icon to the left of the web address (a lock, or sliders).',
    'Set Location to "Allow" for this site.',
    'Reload this page and try again.',
  ];
}
